/**
 * Profile Context
 * 
 * Provides shared user profile state across all components.
 * Fetches and caches profile data from the API.
 * 
 * @module contexts/ProfileContext
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { UserProfileDetailed } from '@/types/user';
import { UserApiService } from '@/services/api';
import { useAuth } from './AuthContext';

interface ProfileContextType {
  profile: UserProfileDetailed | null;
  isLoading: boolean;
  error: string | null;
  refreshProfile: () => Promise<void>;
}

const ProfileContext = createContext<ProfileContextType | null>(null);

interface ProfileProviderProps {
  children: ReactNode;
}

export function ProfileProvider({ children }: ProfileProviderProps) {
  const { user, isAuthenticated } = useAuth();
  const [profile, setProfile] = useState<UserProfileDetailed | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Use ref to track if fetch is in progress (avoids dependency issues)
  const isFetchingRef = useRef(false);

  const fetchProfile = useCallback(async () => {
    if (!user?.id || !isAuthenticated) {
      setProfile(null);
      return;
    }

    // Prevent duplicate fetches if already loading
    if (isFetchingRef.current) {
      return;
    }

    try {
      isFetchingRef.current = true;
      setIsLoading(true);
      setError(null);

      // getUserData is the sole source for all profile fields (lore_user_name,
      // sso_object_id, email, account_verified_flag). The /profile endpoint has
      // been removed (APP-966).
      const mergedProfile = await UserApiService.getUserData(user.id);

      // Strip PII — the app is anonymous and must not retain personal info in state
      delete (mergedProfile as any).first_name;
      delete (mergedProfile as any).last_name;
      delete (mergedProfile as any).birthdate;

      setProfile(mergedProfile);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load profile');
      setProfile(null);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [user?.id, isAuthenticated]);

  // Fetch profile when user changes
  // Note: fetchProfile is stable (memoized), but we depend on user/auth directly to avoid loops
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      fetchProfile();
    } else {
      setProfile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id]); // Intentionally exclude fetchProfile to prevent infinite loops

  const value: ProfileContextType = {
    profile,
    isLoading,
    error,
    refreshProfile: fetchProfile,
  };

  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  );
}

/**
 * Hook to access profile context
 * Must be used within a ProfileProvider
 */
export function useProfile() {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
}

